import { useState, useRef, useEffect, useCallback } from "react";
import { useMonitorControls } from "@/components/monitor/useMonitorControls";
import { useNavigate } from "react-router-dom";

import {
  useWebRTC,
  useMediaRecorder,
  useFeedback,
  useBackgroundActivity,
  useVideoRecovery,
} from "@/hooks";
import { LULLABIES, type Lullaby } from "@/constants";
import { DeviceSelectionView } from "@/features/parent/DeviceSelectionView";
import { MonitoringView } from "@/features/parent/MonitoringView";
import type { BabyDeviceInfo } from "@/features/parent/types";
import { formatRecordingTime, formatTimeAgo } from "@/features/parent/utils";

// ============================================================
// Component
// ============================================================

export function ParentModePage() {
  const navigate = useNavigate();
  // ============================================================
  // State
  // ============================================================

  // Connection states
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState<string | null>(
    null,
  );
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false); // Start with speaker ON for better UX

  // Multi-baby support
  const [babyDevices, setBabyDevices] = useState<BabyDeviceInfo[]>([]);
  const [selectedBaby, setSelectedBaby] = useState<BabyDeviceInfo | null>(null);

  // Audio level for visualization
  const [audioLevel, setAudioLevel] = useState(0);

  // Track speaker state before talk mode for smart restore
  const speakerWasOnBeforeTalkRef = useRef(false);

  // Motion alert notification
  const [motionAlertTimestamp, setMotionAlertTimestamp] = useState<
    number | null
  >(null);
  const [motionAlertAgo, setMotionAlertAgo] = useState("");
  const motionAlertTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const motionAlertIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const motionAudioRef = useRef<HTMLAudioElement | null>(null);

  // Lullaby state
  const [isLullabyPopupOpen, setIsLullabyPopupOpen] = useState(false);
  const [isLullabyPlaying, setIsLullabyPlaying] = useState(false);
  const [currentLullabyId, setCurrentLullabyId] = useState<string | null>(null);

  // ============================================================
  // Refs
  // ============================================================

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const callInitiatedRef = useRef<boolean>(false);
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // ============================================================
  // Callbacks for WebRTC
  // ============================================================

  /**
   * Handle remote stream from WebRTC
   */
  const handleRemoteStream = useCallback((stream: MediaStream) => {
    console.log(
      "[Parent] Remote stream received:",
      stream.getTracks().map((t) => `${t.kind}:${t.enabled}`),
    );
    setRemoteStream(stream);
    setConnectionMessage(null);
    setIsConnecting(false);

    // Clear the connection timeout since we got the stream
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    callInitiatedRef.current = false;
  }, []);

  // ============================================================
  // WebRTC Hook
  // ============================================================

  const {
    connectionState,
    error,
    call,
    disconnect,
    signalingClient,
    isTalking,
    startTalking,
    stopTalking,
  } = useWebRTC({
    role: "parent",
    onRemoteStream: handleRemoteStream,
  });

  // ============================================================
  // Media Recorder Hook
  // ============================================================

  const { isRecording, recordingTime, startRecording, stopRecording } =
    useMediaRecorder({ stream: remoteStream });

  // ============================================================
  // Feedback Hook (sounds & vibrations)
  // ============================================================

  const { trigger: triggerFeedback } = useFeedback();

  // ============================================================
  // Background Activity Hook (keep app alive when minimized)
  // ============================================================

  const { requestPermission: requestNotificationPermission } =
    useBackgroundActivity({
      enabled: !!remoteStream, // Only active when monitoring
      showNotification: true,
      notificationOptions: {
        title: "Baby Monitor Active",
        body: "Keep GoodBaby open to continue monitoring. Tap to return.",
        tag: "goodbaby-monitoring",
      },
      onBackground: () => {
        console.log("[Parent] App went to background while monitoring");
      },
      onForeground: () => {
        console.log("[Parent] App returned to foreground");
      },
    });

  // Video recovery hook - fixes frozen frames when returning from background
  useVideoRecovery(videoRef, remoteStream);

  // Track previous device states for detecting state transitions
  const prevBabyDevicesRef = useRef<
    Map<string, { online: boolean; streaming: boolean }>
  >(new Map());

  // ============================================================
  // Helper Functions
  // ============================================================

  // Dismiss motion alert
  const dismissMotionAlert = useCallback(() => {
    setMotionAlertTimestamp(null);
    setMotionAlertAgo("");
    if (motionAlertTimeoutRef.current) {
      clearTimeout(motionAlertTimeoutRef.current);
      motionAlertTimeoutRef.current = null;
    }
    if (motionAlertIntervalRef.current) {
      clearInterval(motionAlertIntervalRef.current);
      motionAlertIntervalRef.current = null;
    }
  }, []);

  // ============================================================
  // Audio Level Analysis
  // ============================================================

  useEffect(() => {
    if (!remoteStream) {
      setAudioLevel(0);
      return;
    }

    const audioTrack = remoteStream.getAudioTracks()[0];
    if (!audioTrack) {
      console.log("[Parent] No audio track in remote stream");
      return;
    }

    // Initialize AudioContext
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    }

    const ctx = audioContextRef.current;

    // Resume AudioContext if suspended (required for mobile browsers)
    if (ctx.state === "suspended") {
      ctx
        .resume()
        .then(() => {
          console.log("[Parent] AudioContext resumed for visualization");
        })
        .catch((err) => {
          console.warn("[Parent] Failed to resume AudioContext:", err);
        });
    }

    // Create Analyser
    if (!analyserRef.current) {
      analyserRef.current = ctx.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.85;
    }

    const analyser = analyserRef.current;

    // Create Source - disconnect previous if exists
    try {
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
        sourceRef.current = null;
      }
      sourceRef.current = ctx.createMediaStreamSource(remoteStream);
      sourceRef.current.connect(analyser);
      console.log("[Parent] Audio source connected for visualization");
    } catch (err) {
      console.error("[Parent] Error creating audio source:", err);
      return;
    }

    // Pre-allocate data array for performance
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    // Analysis loop (throttled to ~30fps)
    let lastUpdateTime = 0;
    const updateInterval = 33;

    const updateAudioLevel = (timestamp: number) => {
      if (timestamp - lastUpdateTime < updateInterval) {
        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        return;
      }
      lastUpdateTime = timestamp;

      analyser.getByteFrequencyData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i] ?? 0;
      }
      const average = sum / dataArray.length;
      const normalized = Math.min(100, (average / 128) * 100 * 2);

      setAudioLevel(normalized);
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    };

    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
        sourceRef.current = null;
      }
    };
  }, [remoteStream]);

  // ============================================================
  // Connection State Management
  // ============================================================

  useEffect(() => {
    if (
      connectionState === "connected" ||
      connectionState === "failed" ||
      connectionState === "disconnected"
    ) {
      setIsConnecting(false);
      if (connectionState !== "connected") {
        callInitiatedRef.current = false;
        setConnectionMessage(
          "Connection interrupted. Check the baby device and try again.",
        );
      } else {
        setConnectionMessage(null);
      }
    }

    // Auto-reconnect when WebRTC fails but signaling is connected
    // Only attempt if baby is still streaming
    if (
      (connectionState === "failed" || connectionState === "disconnected") &&
      selectedBaby &&
      (selectedBaby.cameraActive || selectedBaby.micActive) &&
      signalingClient.isConnected()
    ) {
      console.log("[Parent] WebRTC failed - attempting reconnect in 2s");

      const reconnectTimeout = setTimeout(() => {
        if (signalingClient.isConnected()) {
          const activeBaby = babyDevices.find(
            (b) => b.cameraActive || b.micActive,
          );

          if (activeBaby) {
            console.log("[Parent] Auto-reconnecting to:", activeBaby.id);
            setSelectedBaby(activeBaby);
            setIsConnecting(true);
            callInitiatedRef.current = true;
            call(activeBaby.id);
          } else {
            console.log("[Parent] No active baby devices - not reconnecting");
            setSelectedBaby(null);
            setRemoteStream(null);
            setIsConnecting(false);
          }
        }
      }, 2000);

      return () => clearTimeout(reconnectTimeout);
    }
  }, [connectionState, selectedBaby, call, babyDevices, signalingClient]);

  // ============================================================
  // Signaling Event Handlers
  // ============================================================

  // Handle signaling reconnection and initial connection
  useEffect(() => {
    const handleConnectionRestored = () => {
      console.log("[Parent] Signaling connection restored");
      // Request all baby devices to broadcast their state immediately
      signalingClient.send({ type: "request-device-states" });
    };

    const handleRegistered = () => {
      console.log(
        "[Parent] Registered with signaling server, requesting device states",
      );
      // Request all baby devices to broadcast their state immediately
      // This ensures we get the current streaming state, not stale data
      signalingClient.send({ type: "request-device-states" });
    };

    signalingClient.on("connection-restored", handleConnectionRestored);
    signalingClient.on("registered", handleRegistered);

    // Periodic ping to keep connection alive and get fresh device list
    // Use faster interval (1.5s) when actively streaming for quicker updates
    const isCurrentlyStreaming = !!remoteStream;
    const pingIntervalMs = isCurrentlyStreaming ? 1500 : 3000;
    const pingInterval = setInterval(() => {
      if (signalingClient.isConnected()) {
        signalingClient.send({ type: "ping" });
      }
    }, pingIntervalMs);

    return () => {
      signalingClient.off("connection-restored", handleConnectionRestored);
      signalingClient.off("registered", handleRegistered);
      clearInterval(pingInterval);
    };
  }, [signalingClient, remoteStream]);

  // Handle device list updates
  useEffect(() => {
    const handleDeviceList = (data: any) => {
      const devices = data.devices || [];
      const babies = devices
        .filter((d: any) => d.role === "baby")
        .map((d: any) => ({
          id: d.id,
          cameraActive: d.cameraActive ?? false,
          micActive: d.micActive ?? false,
          deviceName: d.deviceName,
          batteryLevel: d.batteryLevel,
          isCharging: d.isCharging,
          networkType: d.networkType,
        }));

      // Check for device state transitions and trigger feedback
      const currentDeviceIds = new Set(babies.map((b: BabyDeviceInfo) => b.id));
      const prevDevices = prevBabyDevicesRef.current;

      // Check for devices that went offline
      prevDevices.forEach((prevState, deviceId) => {
        if (prevState.online && !currentDeviceIds.has(deviceId)) {
          console.log("[Parent Feedback] Baby device went offline:", deviceId);
          triggerFeedback("device_offline");
        }
      });

      // Check for devices that came online or changed streaming state
      babies.forEach((baby: BabyDeviceInfo) => {
        const prevState = prevDevices.get(baby.id);
        const isStreaming = baby.cameraActive || baby.micActive;

        if (!prevState) {
          // New device came online
          console.log("[Parent Feedback] Baby device came online:", baby.id);
          triggerFeedback("device_online");
        } else if (!prevState.streaming && isStreaming) {
          // Device started streaming
          console.log(
            "[Parent Feedback] Baby device started streaming:",
            baby.id,
          );
          triggerFeedback("device_online");
        }
      });

      // Update previous state tracking
      const newPrevDevices = new Map<
        string,
        { online: boolean; streaming: boolean }
      >();
      babies.forEach((baby: BabyDeviceInfo) => {
        newPrevDevices.set(baby.id, {
          online: true,
          streaming: baby.cameraActive || baby.micActive,
        });
      });
      prevBabyDevicesRef.current = newPrevDevices;

      // Merge with existing devices - prefer streaming state from existing if incoming shows offline
      // This handles race condition where device-state-changed with accurate state arrives before device-list
      setBabyDevices((prev) => {
        const prevMap = new Map(prev.map((b) => [b.id, b]));

        const mergedBabies = babies.map((baby: BabyDeviceInfo) => {
          const existing = prevMap.get(baby.id);

          // If existing device shows as streaming but incoming doesn't, keep the existing streaming state
          // This happens when device-state-changed arrived before device-list with stale registry data
          if (
            existing &&
            (existing.cameraActive || existing.micActive) &&
            !(baby.cameraActive || baby.micActive)
          ) {
            console.log(
              `[Parent] Preserving streaming state for ${baby.id} (device-list had stale data)`,
            );
            return {
              ...baby,
              cameraActive: existing.cameraActive,
              micActive: existing.micActive,
              batteryLevel: baby.batteryLevel ?? existing.batteryLevel,
              isCharging: baby.isCharging ?? existing.isCharging,
              networkType: baby.networkType ?? existing.networkType,
            };
          }

          return baby;
        });

        // Update selected baby if still in list (using merged data)
        if (selectedBaby) {
          const updated = mergedBabies.find(
            (b: BabyDeviceInfo) => b.id === selectedBaby.id,
          );
          if (updated) {
            // Only update if there's an actual change to avoid unnecessary re-renders
            if (
              updated.cameraActive !== selectedBaby.cameraActive ||
              updated.micActive !== selectedBaby.micActive ||
              updated.batteryLevel !== selectedBaby.batteryLevel
            ) {
              setSelectedBaby(updated);
            }
          } else {
            console.log("[Parent] Selected baby went offline");
            // Trigger feedback for baby going offline while monitoring
            if (remoteStream) {
              triggerFeedback("baby_disconnect");
            }
            setSelectedBaby(null);
            setRemoteStream(null);
            setIsConnecting(false);
            callInitiatedRef.current = false;
          }
        }

        console.log(`[Parent] Found ${mergedBabies.length} baby device(s)`);
        return mergedBabies;
      });
    };

    signalingClient.on("device-list", handleDeviceList);
    return () => signalingClient.off("device-list", handleDeviceList);
  }, [signalingClient, selectedBaby, triggerFeedback, remoteStream]);

  // Handle peer disconnect
  useEffect(() => {
    const handlePeerDisconnected = (data: any) => {
      console.log("[Parent] Peer disconnected:", data.sourceId);
      if (selectedBaby && data.sourceId === selectedBaby.id) {
        // Trigger feedback for baby disconnecting while monitoring
        if (remoteStream) {
          console.log("[Parent Feedback] Baby disconnected while monitoring");
          triggerFeedback("baby_disconnect");
        }
        setRemoteStream(null);
        setIsConnecting(false);
        setSelectedBaby(null);
        callInitiatedRef.current = false;
        // Clear any pending connection timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
      }
    };

    signalingClient.on("peer-disconnected", handlePeerDisconnected);
    return () =>
      signalingClient.off("peer-disconnected", handlePeerDisconnected);
  }, [signalingClient, selectedBaby, triggerFeedback, remoteStream]);

  // Handle device state changes
  useEffect(() => {
    const handleDeviceStateChanged = (data: any) => {
      console.log("[Parent] Baby device state changed:", data);

      // Check for streaming state change and trigger feedback
      const currentBaby = babyDevices.find((b) => b.id === data.deviceId);
      if (currentBaby) {
        const wasStreaming = currentBaby.cameraActive || currentBaby.micActive;
        const nowStreaming = data.cameraActive || data.micActive;

        // Device started streaming (was standby, now streaming)
        if (!wasStreaming && nowStreaming) {
          console.log("[Parent Feedback] Baby device started streaming");
          triggerFeedback("device_online");
        }
        // Device stopped streaming (was streaming, now standby/online-only)
        // For unmonitored devices, notify state change
        // Monitored devices trigger baby_disconnect in the section below
        if (
          wasStreaming &&
          !nowStreaming &&
          selectedBaby?.id !== data.deviceId
        ) {
          console.log(
            "[Parent Feedback] Baby device stopped streaming (now online only)",
          );
          triggerFeedback("device_online");
        }
      }

      // Update the baby device in the list (or add if not present - upsert)
      // This fixes the race condition where device-state-changed arrives before device-list
      setBabyDevices((prev) => {
        const existingIndex = prev.findIndex((b) => b.id === data.deviceId);

        if (existingIndex >= 0) {
          // Update existing device
          return prev.map((baby) =>
            baby.id === data.deviceId
              ? {
                  ...baby,
                  cameraActive: data.cameraActive ?? false,
                  micActive: data.micActive ?? true,
                  batteryLevel: data.batteryLevel,
                  isCharging: data.isCharging,
                  networkType: data.networkType,
                }
              : baby,
          );
        } else {
          // Device not in list yet - add it (this handles the race condition)
          console.log(
            "[Parent] Adding baby device from state-change:",
            data.deviceId,
          );
          return [
            ...prev,
            {
              id: data.deviceId,
              cameraActive: data.cameraActive ?? false,
              micActive: data.micActive ?? true,
              batteryLevel: data.batteryLevel,
              isCharging: data.isCharging,
              networkType: data.networkType,
            },
          ];
        }
      });

      // Update selected baby if it's the one that changed
      if (selectedBaby && selectedBaby.id === data.deviceId) {
        setSelectedBaby((prev) =>
          prev
            ? {
                ...prev,
                cameraActive: data.cameraActive ?? false,
                micActive: data.micActive ?? true,
                batteryLevel: data.batteryLevel,
                isCharging: data.isCharging,
                networkType: data.networkType,
              }
            : null,
        );

        // Clear state if baby stopped streaming
        if (!data.cameraActive && !data.micActive) {
          console.log("[Parent] Baby stopped streaming - resetting state");
          // Trigger feedback for baby disconnecting while monitoring
          if (remoteStream) {
            console.log(
              "[Parent Feedback] Baby stopped streaming while monitoring",
            );
            triggerFeedback("baby_disconnect");
          }
          // Disconnect WebRTC to clean up stale peer connection
          disconnect();
          setRemoteStream(null);
          setIsConnecting(false);
          setSelectedBaby(null);
          callInitiatedRef.current = false;
          // Clear any pending connection timeout
          if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
          }
        }
      }
    };

    signalingClient.on("device-state-changed", handleDeviceStateChanged);
    return () =>
      signalingClient.off("device-state-changed", handleDeviceStateChanged);
  }, [
    signalingClient,
    selectedBaby,
    disconnect,
    babyDevices,
    triggerFeedback,
    remoteStream,
  ]);

  // Handle motion alerts from baby device
  useEffect(() => {
    const handleMotionAlert = (data: any) => {
      console.log("[Parent] Motion alert received:", data);

      // Only show notification if we're actively monitoring
      if (!remoteStream || !selectedBaby) return;

      // Only show if alert is from the baby we're monitoring
      if (data.sourceId && data.sourceId !== selectedBaby.id) return;

      const timestamp = data.timestamp || Date.now();

      // Clear any existing timers
      if (motionAlertTimeoutRef.current) {
        clearTimeout(motionAlertTimeoutRef.current);
      }
      if (motionAlertIntervalRef.current) {
        clearInterval(motionAlertIntervalRef.current);
      }

      // Set the alert
      setMotionAlertTimestamp(timestamp);
      setMotionAlertAgo(formatTimeAgo(timestamp));

      // Play sound
      if (!motionAudioRef.current) {
        motionAudioRef.current = new Audio("/sounds/movement_notification.mp3");
      }
      motionAudioRef.current.currentTime = 0;
      motionAudioRef.current.play().catch((err) => {
        console.warn("[Parent] Failed to play motion sound:", err);
      });

      // Update "ago" text every second
      motionAlertIntervalRef.current = setInterval(() => {
        setMotionAlertAgo(formatTimeAgo(timestamp));
      }, 1000);

      // Auto-dismiss after 2 minutes
      motionAlertTimeoutRef.current = setTimeout(() => {
        setMotionAlertTimestamp(null);
        setMotionAlertAgo("");
        if (motionAlertIntervalRef.current) {
          clearInterval(motionAlertIntervalRef.current);
          motionAlertIntervalRef.current = null;
        }
      }, 120000); // 2 minutes
    };

    signalingClient.on("motion-alert", handleMotionAlert);
    return () => {
      signalingClient.off("motion-alert", handleMotionAlert);
      // Cleanup timers on unmount
      if (motionAlertTimeoutRef.current) {
        clearTimeout(motionAlertTimeoutRef.current);
      }
      if (motionAlertIntervalRef.current) {
        clearInterval(motionAlertIntervalRef.current);
      }
    };
  }, [signalingClient, remoteStream, selectedBaby]);

  // ============================================================
  // Media Playback
  // ============================================================

  // Determine stream type from actual tracks
  const hasVideoTrack = remoteStream
    ? remoteStream.getVideoTracks().some((t) => t.readyState === "live")
    : false;

  const hasAudioTrack = remoteStream
    ? remoteStream.getAudioTracks().some((t) => t.readyState === "live")
    : false;

  const hasVideo = hasVideoTrack;
  const hasAudio = hasAudioTrack;
  const isStreaming = !!remoteStream;

  // Attach stream to appropriate element
  useEffect(() => {
    if (!remoteStream) return;

    console.log(
      `[Parent] Attaching stream. hasVideo: ${hasVideo}, tracks:`,
      remoteStream.getTracks().map((t) => `${t.kind}:${t.readyState}`),
    );

    if (!hasVideo) {
      // Audio-only: use audio element
      const audio = audioRef.current;
      if (!audio) return;

      // Clear video element
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }

      audio.srcObject = remoteStream;
      audio.muted = isMuted;

      audio.play().catch((err) => {
        console.warn("[Parent] Audio play failed:", err);
      });
    } else {
      // Video+audio: use video element
      const video = videoRef.current;
      if (!video) return;

      // Clear audio element
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.srcObject = null;
      }

      video.srcObject = remoteStream;
      video.muted = isMuted;

      video.play().catch((err) => {
        console.warn("[Parent] Video play failed:", err);
      });
    }
  }, [remoteStream, hasVideo, isMuted]);

  // Handle mute state changes
  useEffect(() => {
    if (hasVideo && videoRef.current && remoteStream) {
      videoRef.current.muted = isMuted;
    } else if (!hasVideo && audioRef.current && remoteStream) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted, remoteStream, hasVideo]);

  // ============================================================
  // Auto Picture-in-Picture
  // ============================================================

  /**
   * Automatically enter Picture-in-Picture mode when the app goes to background
   * while actively monitoring with video. This allows parents to continue
   * watching the baby feed while using other apps.
   *
   * Browser Support:
   * - Chrome/Edge: Full support via document.pictureInPictureEnabled
   * - Firefox: Full support via document.pictureInPictureEnabled
   * - Safari macOS: Full support via document.pictureInPictureEnabled
   * - Safari iOS: Limited - uses webkit prefixed API, requires user gesture
   *   (auto-PiP not supported, but we handle gracefully)
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Check if PiP is supported
    const isPiPSupported =
      "pictureInPictureEnabled" in document && document.pictureInPictureEnabled;

    // Check for webkit PiP (Safari)
    const isWebkitPiPSupported =
      "webkitSupportsPresentationMode" in video &&
      typeof (video as any).webkitSetPresentationMode === "function";

    if (!isPiPSupported && !isWebkitPiPSupported) {
      console.log("[Parent] Picture-in-Picture not supported in this browser");
      return;
    }

    /**
     * Enter Picture-in-Picture mode
     * Handles both standard API and webkit (Safari) fallback
     */
    const enterPiP = async () => {
      try {
        // Don't enter PiP if already in PiP
        if (document.pictureInPictureElement === video) return;
        if ((video as any).webkitPresentationMode === "picture-in-picture")
          return;

        // Must have video content
        if (!hasVideo || !remoteStream) return;

        // Ensure video has loaded metadata (required for PiP)
        if (video.readyState < 2) {
          console.log("[Parent] Video not ready for PiP yet");
          return;
        }

        if (isPiPSupported) {
          await video.requestPictureInPicture();
          console.log("[Parent] Entered Picture-in-Picture mode");
        } else if (isWebkitPiPSupported) {
          // Safari webkit fallback
          (video as any).webkitSetPresentationMode("picture-in-picture");
          console.log("[Parent] Entered webkit Picture-in-Picture mode");
        }
      } catch (err: any) {
        // Common errors:
        // - NotAllowedError: No user gesture (common on iOS)
        // - InvalidStateError: Video not playing or no video track
        // - NotSupportedError: PiP not allowed for this video
        if (err.name === "NotAllowedError") {
          console.log("[Parent] PiP requires user gesture - skipping auto-PiP");
        } else {
          console.warn("[Parent] Failed to enter PiP:", err.message);
        }
      }
    };

    /**
     * Exit Picture-in-Picture mode
     */
    const exitPiP = async () => {
      try {
        if (document.pictureInPictureElement === video) {
          await document.exitPictureInPicture();
          console.log("[Parent] Exited Picture-in-Picture mode");
        } else if (
          (video as any).webkitPresentationMode === "picture-in-picture"
        ) {
          (video as any).webkitSetPresentationMode("inline");
          console.log("[Parent] Exited webkit Picture-in-Picture mode");
        }
      } catch (err: any) {
        console.warn("[Parent] Failed to exit PiP:", err.message);
      }
    };

    /**
     * Handle page visibility changes
     * Enter PiP when page becomes hidden, exit when visible
     */
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is now hidden (minimized, switched tab/app)
        enterPiP();
      } else {
        // Page is now visible (user returned)
        exitPiP();
      }
    };

    /**
     * Handle page blur (helps catch when PWA loses focus on mobile)
     * This is a backup for visibility change which may not fire in all scenarios
     */
    const handlePageBlur = () => {
      // Small delay to avoid triggering on quick interactions
      setTimeout(() => {
        if (document.hidden) {
          enterPiP();
        }
      }, 100);
    };

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handlePageBlur);

    // Handle PiP events for logging
    const handleEnterPiP = () => {
      console.log("[Parent] PiP mode activated");
    };

    const handleLeavePiP = () => {
      console.log("[Parent] PiP mode deactivated");
    };

    video.addEventListener("enterpictureinpicture", handleEnterPiP);
    video.addEventListener("leavepictureinpicture", handleLeavePiP);

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handlePageBlur);
      video.removeEventListener("enterpictureinpicture", handleEnterPiP);
      video.removeEventListener("leavepictureinpicture", handleLeavePiP);

      // Exit PiP on cleanup (component unmount)
      exitPiP();
    };
  }, [hasVideo, remoteStream]);

  // ============================================================
  // Action Handlers
  // ============================================================

  const handleConnect = async (baby: BabyDeviceInfo) => {
    if (callInitiatedRef.current || !(baby.cameraActive || baby.micActive))
      return;
    setConnectionMessage(null);
    // Request notification permission on user interaction (best time to ask)
    requestNotificationPermission();

    // Clear any existing timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    setSelectedBaby(baby);
    setIsConnecting(true);
    callInitiatedRef.current = true;

    // Connection timeout - use ref to properly track latest state
    connectionTimeoutRef.current = setTimeout(() => {
      // Only timeout if we haven't received a stream yet
      if (callInitiatedRef.current) {
        console.warn("[Parent] Connection timeout");
        setConnectionMessage(
          "Could not connect. Check that the baby device is still streaming, then try again.",
        );
        setIsConnecting(false);
        callInitiatedRef.current = false;
        connectionTimeoutRef.current = null;
      }
    }, 15000);

    try {
      await call(baby.id);
    } catch (err) {
      console.error("[Parent] Failed to connect:", err);
      setConnectionMessage("Could not connect. Please try again.");
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      setIsConnecting(false);
      callInitiatedRef.current = false;
    }
  };

  const handleDisconnect = () => {
    setConnectionMessage(null);
    // Trigger feedback for parent manually disconnecting
    if (remoteStream) {
      console.log("[Parent Feedback] Parent clicked stop while monitoring");
      triggerFeedback("parent_disconnect");
    }
    // Clear connection timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    disconnect();
    setRemoteStream(null);
    setIsConnecting(false);
    callInitiatedRef.current = false;
    setSelectedBaby(null);
    // Note: Don't reconnect signaling here - we just need to stop the WebRTC call
    // The signaling connection should remain active for device discovery
  };

  const toggleMute = () => {
    // Resume AudioContext on user interaction (required for mobile browsers)
    if (audioContextRef.current?.state === "suspended") {
      audioContextRef.current.resume().then(() => {
        console.log("[Parent] AudioContext resumed via user interaction");
      });
    }

    // If talking, clicking speaker button should stop talking and ensure speaker is ON
    if (isTalking) {
      stopTalking();
      setIsMuted(false);
      console.log(
        "[Parent] Speaker clicked while talking - stopped talk, speaker ON",
      );
      return;
    }

    setIsMuted((prev) => !prev);
  };

  /**
   * Toggle talk mode (parent talking to baby)
   * Automatically mutes speaker while talking to prevent feedback
   * Restores previous speaker state when done talking
   */
  const toggleTalk = useCallback(async () => {
    if (isTalking) {
      // Stop talking
      stopTalking();
      // Restore speaker state (unmute if it was on before talking)
      if (speakerWasOnBeforeTalkRef.current) {
        setIsMuted(false);
      }
      console.log(
        `[Parent] Stopped talking, speaker restored to: ${speakerWasOnBeforeTalkRef.current ? "ON" : "OFF"}`,
      );
    } else {
      // Start talking
      // Save current speaker state before muting
      speakerWasOnBeforeTalkRef.current = !isMuted;
      // Mute speaker to prevent feedback
      setIsMuted(true);
      await startTalking();
      console.log(
        `[Parent] Started talking, speaker was: ${speakerWasOnBeforeTalkRef.current ? "ON" : "OFF"}`,
      );
    }
  }, [isTalking, isMuted, startTalking, stopTalking]);

  // Suppress unused variable warning while feature is disabled
  void toggleTalk;

  // ============================================================
  // Lullaby Controls
  // ============================================================

  /**
   * Send lullaby control command to baby device
   */
  const sendLullabyControl = useCallback(
    (action: "play" | "stop", lullabyId?: string) => {
      if (!selectedBaby) return;

      signalingClient.send({
        type: "lullaby-control",
        targetId: selectedBaby.id,
        action,
        lullabyId,
      });

      console.log(
        `[Parent] Sent lullaby ${action}${lullabyId ? ` (${lullabyId})` : ""} to baby`,
      );
    },
    [selectedBaby, signalingClient],
  );

  /**
   * Handle lullaby selection from popup
   */
  const handleLullabySelect = useCallback(
    (lullaby: Lullaby) => {
      sendLullabyControl("play", lullaby.id);
      setIsLullabyPopupOpen(false);
    },
    [sendLullabyControl],
  );

  /**
   * Stop the currently playing lullaby
   */
  const handleLullabyStop = useCallback(() => {
    sendLullabyControl("stop");
    setIsLullabyPopupOpen(false);
  }, [sendLullabyControl]);

  /**
   * Toggle lullaby popup visibility
   */
  const toggleLullabyPopup = useCallback(() => {
    setIsLullabyPopupOpen((prev) => !prev);
  }, []);

  /**
   * Get the currently playing lullaby info
   */
  const getCurrentLullaby = useCallback((): Lullaby | null => {
    if (!currentLullabyId) return null;
    return LULLABIES.find((l) => l.id === currentLullabyId) || null;
  }, [currentLullabyId]);

  // Listen for lullaby state updates from baby device
  useEffect(() => {
    const handleLullabyState = (data: any) => {
      console.log("[Parent] Lullaby state received:", data);

      // Only update if it's from the baby we're monitoring
      if (selectedBaby && data.sourceId === selectedBaby.id) {
        setIsLullabyPlaying(data.playing);
        setCurrentLullabyId(data.lullabyId || null);
      }
    };

    signalingClient.on("lullaby-state", handleLullabyState);
    return () => signalingClient.off("lullaby-state", handleLullabyState);
  }, [signalingClient, selectedBaby]);

  // Reset lullaby state when disconnecting
  useEffect(() => {
    if (!remoteStream) {
      setIsLullabyPlaying(false);
      setCurrentLullabyId(null);
      setIsLullabyPopupOpen(false);
    }
  }, [remoteStream]);

  // Stop lullaby when parent disconnects (before cleanup)
  const handleDisconnectWithLullaby = useCallback(() => {
    // Stop lullaby before disconnecting
    if (isLullabyPlaying && selectedBaby) {
      sendLullabyControl("stop");
    }
    handleDisconnect();
  }, [isLullabyPlaying, selectedBaby, sendLullabyControl, handleDisconnect]);

  // Stop lullaby when recording starts
  useEffect(() => {
    if (isRecording && isLullabyPlaying) {
      console.log("[Parent] Recording started, stopping lullaby");
      sendLullabyControl("stop");
    }
  }, [isRecording, isLullabyPlaying, sendLullabyControl]);

  const { controlsVisible, onFocusCapture, onBlurCapture, onScreenTap } =
    useMonitorControls(isStreaming, isLullabyPopupOpen);

  if (isStreaming && selectedBaby) {
    return (
      <MonitoringView
        selectedBaby={
          babyDevices.find((baby) => baby.id === selectedBaby.id) ??
          selectedBaby
        }
        remoteStream={remoteStream}
        hasVideo={hasVideo}
        hasAudio={hasAudio}
        audioLevel={audioLevel}
        controlsVisible={controlsVisible}
        isMuted={isMuted}
        isRecording={isRecording}
        recordingTime={recordingTime}
        isLullabyPopupOpen={isLullabyPopupOpen}
        isLullabyPlaying={isLullabyPlaying}
        currentLullabyId={currentLullabyId}
        currentLullaby={getCurrentLullaby()}
        motionAlertTimestamp={motionAlertTimestamp}
        motionAlertAgo={motionAlertAgo}
        videoRef={videoRef}
        audioRef={audioRef}
        onScreenTap={onScreenTap}
        onFocusCapture={onFocusCapture}
        onBlurCapture={onBlurCapture}
        connectionState={connectionState}
        onToggleLullabyPopup={toggleLullabyPopup}
        onCloseLullabyPopup={() => setIsLullabyPopupOpen(false)}
        onSelectLullaby={handleLullabySelect}
        onStopLullaby={handleLullabyStop}
        onDismissMotionAlert={dismissMotionAlert}
        onToggleMute={toggleMute}
        onToggleRecording={isRecording ? stopRecording : startRecording}
        onDisconnect={handleDisconnectWithLullaby}
        formatRecordingTime={formatRecordingTime}
      />
    );
  }
  return (
    <DeviceSelectionView
      babyDevices={babyDevices}
      selectedBaby={selectedBaby}
      isConnecting={isConnecting}
      error={connectionMessage || error}
      onConnect={handleConnect}
      onOpenSupport={() => navigate("/support")}
    />
  );
}
